import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FieldArray } from 'formik';
import { Button, Col, Row } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import { cloneDeep } from 'lodash';
import { useDispatch } from 'react-redux';
import SweetAlert from 'react-bootstrap-sweetalert';
import uuid from 'react-uuid';
import { TbCirclePlusFilled, TbTrashX, TbX } from 'react-icons/tb';
import {
  FormDatePickerField,
  FormInputField,
  FormSelectField,
} from '../../common/components/formFields';
import {
  VehicleServiceHistory,
  VehicleServiceHistoryName,
} from '../../constants/VehicleServiceHistory';
import { ServiceTypeName } from '../../constants/ServiceType';
import { constantToSelect } from '../../common/helpers';
import { UploadFileTypes } from '../../constants/UploadFileTypes';
import { addPhoto } from '../VehicleActions';

const vehicleServiceHistories = constantToSelect(VehicleServiceHistoryName);
const serviceTypeOptions = constantToSelect(ServiceTypeName);

function VehicleServiceHistoryForm({ form, services }) {
  const dispatch = useDispatch();
  const [files, setFiles] = useState([]);
  const [showRemoveServiceAlert, setShowRemoveServiceAlert] = useState(false);
  const [removeServiceIndex, setRemoveServiceIndex] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    acceptedFiles.map(async (file) => {
      setPreview(file);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    form.values.newServiceHistoryPhotoRecords = files;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  const changeServiceHistory = (serviceHistory) => {
    if (serviceHistory?.value === VehicleServiceHistory.NONE) {
      for (let i = 0; i < form.values.serviceHistoryPhotoRecords.length; i++) {
        form.values.serviceHistoryPhotoRecordsToDelete.push(
          form.values.serviceHistoryPhotoRecords[i].mediaMetadataId,
        );
      }
      form.setFieldValue('serviceHistoryPhotoRecords', []);
      form.setFieldValue('services', []);
      setFiles([]);
    } else if (!form?.values?.services || form?.values?.services?.length === 0) {
      form.setFieldValue('services', [
        {
          date: '',
          serviceType: '',
          mileage: '',
          serviceName: '',
        },
      ]);
    }
  };

  const setPreview = async (file) => {
    if (file && file?.type.includes(UploadFileTypes.IMAGE)) {
      const photo = await dispatch(addPhoto(file));
      if (photo) {
        file = {
          ...photo,
        };
        file.preview = photo.fileUrl;

        setFiles((prevState) => {
          const files = cloneDeep(prevState);
          files.push(file);
          return files;
        });
      }
    }
  };
  const { getRootProps, getInputProps } = useDropzone({
    accept: 'image/*',
    onDrop,
    noDrag: true,
  });

  const removeImage = (event, fileId) => {
    setFiles((prevState) => prevState.filter((item) => item.id !== fileId));
    form.values.serviceHistoryPhotoRecords = form.values.serviceHistoryPhotoRecords.filter(
      (item) => item.mediaMetadataId !== fileId,
    );
    form.values.serviceHistoryPhotoRecordsToDelete = [
      ...form.values.serviceHistoryPhotoRecordsToDelete,
      fileId,
    ];
    event.stopPropagation();
  };

  const thumbs = files.map((file) => (
    <div className="thumb" key={file.id}>
      <div className="thumb-inner" role="button" tabIndex="0">
        <img src={file.preview} className="img" alt="preview" />
        <div
          className="remove-image"
          role="presentation"
          onClick={(e) => removeImage(e, file.id)}
        >
          <TbTrashX color="red" size={30} />
        </div>
      </div>
    </div>
  ));

  return (
    <>
      <Row>
        <Col>
          <h4 className="my-2">Service history</h4>
        </Col>
      </Row>

      <Row>
        <Col md="4">
          <FormSelectField
            name="serviceHistory"
            label="Service history"
            options={vehicleServiceHistories}
            onChangeHandler={changeServiceHistory}
          />
        </Col>
      </Row>
      <Row className="media-upload">
        <Col>
          <aside className="thumbs-container">
            {form?.values?.serviceHistoryPhotoRecords?.map((photo) => (
              <div className="thumb" key={photo.mediaMetadataId}>
                <div className="thumb-inner">
                  <div
                    className="remove-image"
                    role="presentation"
                    onClick={(e) => removeImage(e, photo.mediaMetadataId)}
                  >
                    <TbTrashX color="red" size={30} />
                  </div>
                  <img
                    key={photo.mediaMetadataId}
                    src={photo.fileUrl}
                    alt="Vehicle thumbnail"
                    className="img"
                  />
                </div>
              </div>
            ))}
          </aside>
        </Col>
      </Row>
      {form?.values?.serviceHistory?.value !== VehicleServiceHistory.NONE && (
        <>
          <Row className="media-upload mb-3">
            <Col col="4">
              <aside className="thumbs-container">
                {thumbs}
                <div {...getRootProps()} className="add-button">
                  <input {...getInputProps()} />
                  <TbCirclePlusFilled color="#252153" size={50} />
                </div>
              </aside>
            </Col>
          </Row>
          <FieldArray
            name="services"
            render={(arrayHelpers) => (
              <div>
                {services.map((service, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={`services.${index}`}>
                    {/* eslint-disable-next-line react/no-array-index-key */}
                    <Row key={index} className="position-relative">
                      <Col
                        key={uuid()}
                        md="12"
                        className="d-flex justify-content-end remove-service-history"
                      >
                        <Button
                          key={uuid()}
                          className="btn-link"
                          color="primary"
                          size="sm"
                          type="button"
                          onClick={() => {
                            setRemoveServiceIndex(index);
                            setShowRemoveServiceAlert(true);
                          }}
                        >
                          <TbX color="white" size={14} />
                        </Button>
                      </Col>
                      <Col md="3">
                        <FormDatePickerField
                          name={`services.${index}.date`}
                          label="Date of Service"
                          required
                        />
                      </Col>
                      <Col md="3">
                        <FormSelectField
                          name={`services.${index}.serviceType`}
                          label="Service Type"
                          options={serviceTypeOptions}
                          required
                        />
                      </Col>
                      <Col md="3">
                        <FormInputField
                          name={`services.${index}.mileage`}
                          label="Mileage"
                          required
                        />
                      </Col>
                      <Col md="3">
                        <FormInputField
                          name={`services.${index}.serviceName`}
                          label="Service"
                          required
                        />
                      </Col>
                    </Row>
                    <SweetAlert
                      danger
                      key={uuid()}
                      showCancel={false}
                      showConfirm={false}
                      show={removeServiceIndex === index && showRemoveServiceAlert}
                      title="Warning!"
                      onConfirm={() => {
                        setShowRemoveServiceAlert(false);
                        arrayHelpers.remove(index);
                      }}
                      onCancel={() => setShowRemoveServiceAlert(false)}
                    >
                      {(renderProps) => (
                        <div>
                          <p>Are you sure to remove a service?</p>
                          <div className="d-flex justify-content-center gap-2 mt-3">
                            <Button
                              className="rounded-pill btn-soft-primary"
                              onClick={renderProps.cancel}
                            >
                              Cancel
                            </Button>
                            <Button
                              className="rounded-pill btn-danger"
                              onClick={renderProps.confirm}
                            >
                              Yes, Remove
                            </Button>
                          </div>
                        </div>
                      )}
                    </SweetAlert>
                  </div>
                ))}
                <Row className="mb-3">
                  <Button
                    className="rounded-pill btn-light"
                    type="button"
                    onClick={() =>
                      arrayHelpers.push({
                        date: '',
                        serviceType: '',
                        mileage: '',
                        serviceName: '',
                      })
                    }
                  >
                    Add another service
                  </Button>
                </Row>
              </div>
            )}
          />
        </>
      )}
    </>
  );
}

VehicleServiceHistoryForm.propTypes = {
  form: PropTypes.object.isRequired,
  services: PropTypes.array.isRequired,
};

export default VehicleServiceHistoryForm;
